import { toConditionEntry, isV2Format, isV1Format } from './formats';
import { normalizeToV2, resolveConditionToEntry } from './convert';

/**
 * Extract all referenced task IDs from any unlockConditions format.
 * Returns a flat array of taskId strings.
 * @param {Array<{id: string}>|null} [indexParentTasks] - when conditions use `task: n` (clipboard), resolve against this ordered list
 */
export function getAllConditionTaskIds(unlockConditions, indexParentTasks = null) {
  if (!unlockConditions) return [];

  if (isV2Format(unlockConditions)) {
    const ids = [];
    for (const block of unlockConditions.blocks || []) {
      for (const cond of block.conditions || []) {
        const entry = resolveConditionToEntry(cond, indexParentTasks);
        if (entry?.taskId) ids.push(entry.taskId);
      }
    }
    return ids;
  }

  if (isV1Format(unlockConditions)) {
    return unlockConditions
      .map((c) => resolveConditionToEntry(c, indexParentTasks))
      .filter(Boolean)
      .map((e) => e.taskId);
  }

  return [];
}

/**
 * Check if a specific taskId exists in any unlockConditions format.
 */
export function hasConditionForTask(unlockConditions, taskId, indexParentTasks = null) {
  return getAllConditionTaskIds(unlockConditions, indexParentTasks).includes(taskId);
}

/**
 * Remove a specific taskId from unlockConditions (any format).
 * Returns the updated value in the same format it was given.
 */
export function removeTaskFromConditions(unlockConditions, taskIdToRemove) {
  if (!unlockConditions) return unlockConditions;

  if (isV2Format(unlockConditions)) {
    const newBlocks = unlockConditions.blocks
      .map(block => ({
        ...block,
        conditions: block.conditions.filter(c => c.taskId !== taskIdToRemove),
      }))
      .filter(block => block.conditions.length > 0);

    if (newBlocks.length > 0) {
      newBlocks[0] = { ...newBlocks[0], outerOperator: null };
    }

    return { version: 2, blocks: newBlocks };
  }

  if (isV1Format(unlockConditions)) {
    const filtered = unlockConditions.filter(c => c.taskId !== taskIdToRemove);
    if (filtered.length > 0 && filtered[0].operator !== null) {
      filtered[0] = { ...filtered[0], operator: null };
    }
    return filtered;
  }

  return unlockConditions;
}

/**
 * Replace a taskId in unlockConditions (any format).
 * Used for rewiring dependencies when inserting nodes on edges.
 */
export function replaceTaskInConditions(unlockConditions, oldTaskId, newTaskId, branchMatch = null) {
  if (!unlockConditions) return unlockConditions;

  if (isV2Format(unlockConditions)) {
    return {
      ...unlockConditions,
      blocks: unlockConditions.blocks.map(block => ({
        ...block,
        conditions: block.conditions.map(c => {
          if (c.taskId === oldTaskId) {
            const branchMatches = !branchMatch || !c.branch || c.branch === branchMatch;
            if (branchMatches) return { ...c, taskId: newTaskId };
          }
          return c;
        }),
      })),
    };
  }

  if (isV1Format(unlockConditions)) {
    return unlockConditions.map(c => {
      if (c.taskId === oldTaskId) {
        const branchMatches = !branchMatch || !c.branch || c.branch === branchMatch;
        if (branchMatches) return { ...c, taskId: newTaskId };
      }
      return c;
    });
  }

  return unlockConditions;
}

/**
 * Add a task to unlockConditions as AND in the first block.
 * If v1 format, converts to v2 first.
 * Returns v2 format.
 */
export function addTaskToConditions(unlockConditions, taskId, branch = null) {
  const v2 = normalizeToV2(unlockConditions);
  const entry = toConditionEntry({ taskId, branch });

  if (v2.blocks.length === 0) {
    return {
      version: 2,
      blocks: [{ conditions: [entry], innerOperator: 'AND', outerOperator: null }],
    };
  }

  const newBlocks = [...v2.blocks];
  newBlocks[0] = {
    ...newBlocks[0],
    conditions: [...newBlocks[0].conditions, entry],
  };

  return { version: 2, blocks: newBlocks };
}

/**
 * Check if unlockConditions has any actual task references (any format).
 */
export function hasAnyConditions(unlockConditions, indexParentTasks = null) {
  return getAllConditionTaskIds(unlockConditions, indexParentTasks).length > 0;
}

/**
 * Extract all condition entries (taskId + branch) from any format.
 * Returns array of { taskId, branch } objects.
 * @param {Array<{id: string, nodeType?: string, options?: Array}>|null} [indexParentTasks] - for clipboard-style { task: n } refs
 */
export function getAllConditionEntries(unlockConditions, indexParentTasks = null) {
  if (!unlockConditions) return [];

  if (isV2Format(unlockConditions)) {
    const entries = [];
    for (const block of unlockConditions.blocks || []) {
      for (const cond of block.conditions || []) {
        const entry = resolveConditionToEntry(cond, indexParentTasks);
        if (entry?.taskId) entries.push(entry);
      }
    }
    return entries;
  }

  if (isV1Format(unlockConditions)) {
    return unlockConditions
      .map((c) => resolveConditionToEntry(c, indexParentTasks))
      .filter(Boolean);
  }

  return [];
}

/**
 * Remap all task IDs in unlockConditions using an ID map.
 * Works with both v1 and v2 formats. Returns the same format.
 */
export function remapConditionTaskIds(unlockConditions, idMap) {
  if (!unlockConditions) return unlockConditions;

  if (isV2Format(unlockConditions)) {
    return {
      ...unlockConditions,
      blocks: unlockConditions.blocks.map(block => ({
        ...block,
        conditions: block.conditions.map(c => {
          if (c.taskId && idMap[c.taskId]) {
            return { ...c, taskId: idMap[c.taskId] };
          }
          return c;
        }),
      })),
    };
  }

  if (isV1Format(unlockConditions)) {
    return unlockConditions.map(cond => {
      if (cond && cond.taskId && idMap[cond.taskId]) {
        return { ...cond, taskId: idMap[cond.taskId] };
      }
      return cond;
    });
  }

  return unlockConditions;
}
